import React from 'react';
import { useI18n } from '../utils/i18n';
import { Ticket } from 'lucide-react';
import TicketCard, { isTicketOverdue } from './ui/TicketCard';
import ListState from './ui/ListState';
import { ticketPasstZurSuche } from '../utils/suchFilter'; // #572

// Welche Tickets die Liste zeigt (Suche, Vorgangs-Tickets ausblenden). Als
// eigene Funktion, damit die Mobilansicht ihren Startreiter nach genau der
// Liste waehlt, die sie danach anzeigt. Inhalt unveraendert aus dem useMemo.
export function sichtbareDashboardTickets(tickets, { searchFilter, useAssigneePerspective = false, filterProjectLinked = true } = {}) {
  // #572: über utils/suchFilter, damit auch Kunde, Kurzform und Ansprechpartner
  // gefunden werden; Beschreibung und Kategorie wie bisher.
  const applySearch = (list) => {
    if (!searchFilter || searchFilter.trim() === '') return list;
    return list.filter(ticket => ticketPasstZurSuche(ticket, searchFilter, [ticket.description, ticket.category]));
  };

  const hasNewForContext = (t) => {
    if (useAssigneePerspective) {
      return !!(t.isNewTicketForAssignee || t.hasNewActivityForAssignee || t.hasNewEmailsForAssignee || t.hasNewCommentsForAssignee || t.hasNewAttachmentsForAssignee);
    }
    return !!(t.isNewTicket || t.hasNewActivity || t.hasNewEmails || t.hasNewComments || t.hasNewAttachments);
  };

  // 1) Optional Suche anwenden
  const afterSearch = applySearch(tickets);

  // 2) Optional: Projekt-/Unterprojekt-Tickets ausblenden, außer wenn
  //    (a) keinem User zugeordnet (unassigned) ODER
  //    (b) geplant & überfällig & nicht abgeschlossen ODER
  //    (c) neue Aktivität (rote Ecke) vorliegt
  const result = afterSearch.filter(ticket => {
    if (!filterProjectLinked) return true;
    const isProjectLinked = ((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0));
    if (!isProjectLinked) return true;
    
    // Unassigned Tickets mit Projekt/Unterprojekt immer anzeigen
    const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
    if (isUnassigned) return true;
    
    const overdue = isTicketOverdue(ticket);
    const notCompleted = ticket.status !== 'abgeschlossen';
    const notPlanned = !ticket.isPlanned;
    if (useAssigneePerspective) {
      return (overdue && notCompleted) || notPlanned || hasNewForContext(ticket);
    }
    return (overdue && notCompleted) || hasNewForContext(ticket);
  });

  return result;
}

const DashboardTicketList = ({ tickets, onTicketClick, onTicketUpdate, searchFilter, useAssigneePerspective = false, filterProjectLinked = true, completedArchivedOpacity = 0.6, status = 'ready', onRetry = null, touchModus = false }) => {
  const { t } = useI18n();

  // Filter tickets based on search filter
  const filteredTickets = React.useMemo(
    () => sichtbareDashboardTickets(tickets, { searchFilter, useAssigneePerspective, filterProjectLinked }),
    [tickets, searchFilter, useAssigneePerspective, filterProjectLinked]
  );

  if (!filteredTickets || filteredTickets.length === 0) {
    // #256: Erst der Ladezustand, dann der Fehlerfall — sonst behauptet die
    // Spalte beim Kaltstart "Keine offenen Tickets", obwohl noch gar nichts
    // abgerufen wurde. Beides gilt nur fuer die wirklich leere Liste.
    const hatTickets = Array.isArray(tickets) && tickets.length > 0;
    if (!hatTickets && status === 'loading') {
      return <ListState variant="loading" />;
    }
    if (!hatTickets && status === 'error') {
      return <ListState variant="error" onRetry={onRetry} />;
    }

    const isSearchActive = searchFilter && searchFilter.trim() !== '';
    return (
      <ListState
        icon={<Ticket size={32} style={{ opacity: 0.3 }} aria-hidden="true" />}
        message={isSearchActive ? t('common.noTicketsFound', 'Keine Tickets gefunden') : t('common.noOpenTickets', 'Keine offenen Tickets')}
        hint={isSearchActive ? t('common.tryOtherSearchTerms', 'Versuchen Sie andere Suchbegriffe') : null}
      />
    );
  }

  return (
    <div className="dashboard-ticket-list">
      {/* #138 (W7): Karte kommt aus ui/TicketCard; Drag-Protokoll
          (dashboard-ticket + window-Events) bleibt hier beim Aufrufer. */}
      {filteredTickets.map((ticket) => (
        <TicketCard
          key={ticket.id}
          ticket={ticket}
          useAssigneePerspective={useAssigneePerspective}
          showAssignedTo={!useAssigneePerspective}
          onClick={onTicketClick ? () => onTicketClick(ticket) : undefined}
          // Mobilansicht: kein Ziehen, kein Kontextmenue per Lange-Druecken
          enableContextMenu={!touchModus}
          onTicketUpdate={onTicketUpdate}
          style={{ opacity: (ticket.status === 'abgeschlossen' || ticket.isArchived) ? completedArchivedOpacity : (ticket.status === 'wartend' ? 0.6 : 1) }}
          draggable={!touchModus}
          onDragStart={(e) => {
            try { 
              e.dataTransfer.effectAllowed = 'move';
              const payload = { type: 'dashboard-ticket', ticketId: ticket.id, ticketData: { id: ticket.id, title: ticket.title, ticketNumber: ticket.ticketNumber, priority: ticket.priority, status: ticket.status, projectId: ticket.projectId || null, subprojectId: ticket.subprojectId || null } };
              e.dataTransfer.setData('text/plain', JSON.stringify(payload));
              try { e.dataTransfer.setData('application/json', JSON.stringify(payload)); } catch(_) {}
              // Unsichtbares Drag-Image setzen, damit der Cursor nicht verdeckt
              try {
                const el = document.createElement('div');
                el.style.position = 'fixed';
                el.style.top = '0';
                el.style.left = '0';
                el.style.width = '1px';
                el.style.height = '1px';
                el.style.opacity = '0';
                el.style.pointerEvents = 'none';
                document.body.appendChild(el);
                e.dataTransfer.setDragImage(el, 0, 0);
                setTimeout(() => { try { document.body.removeChild(el); } catch(_) {} }, 0);
              } catch(_) {}
              // Globales Event für eingebettete Kalender (DayCalendarPanel), damit dieser ohne dataTransfer lesen kann
              try { window.dispatchEvent(new CustomEvent('dashboardTicketDragStart', { detail: { id: ticket.id, title: ticket.title, ticketNumber: ticket.ticketNumber, priority: ticket.priority, status: ticket.status, projectId: ticket.projectId || null, subprojectId: ticket.subprojectId || null } })); } catch(_) {}
            } catch(_) {} 
            try { e.currentTarget.classList.add('dragging'); } catch(_) {}
          }}
          onDragEnd={(e) => { try { e.currentTarget.classList.remove('dragging'); } catch(_) {} try { window.dispatchEvent(new Event('dashboardTicketDragEnd')); } catch(_) {} }}
        />
      ))}
    </div>
  );
};

export default DashboardTicketList; 